import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Save, Loader2, MousePointer, Type, Image, 
  Layout, Layers, Settings, ChevronRight, X, Check,
  Monitor, Smartphone, Tablet, Undo, Redo, Eye
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { adminFetch, isAdminAuthenticated } from '@/utils/adminAuth';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Editable sections with their content
const PAGE_SECTIONS = {
  home: {
    name: 'Homepage',
    sections: [
      {
        id: 'hero',
        name: 'Hero Section',
        icon: Layout,
        elements: [
          { id: 'hero_badge', label: 'Badge Text', type: 'text', value: 'AFRO-BRAZILIAN MUSIC' },
          { id: 'hero_title', label: 'Main Title', type: 'text', value: 'Escola de Samba Casa Samba' },
          { id: 'hero_title_2', label: 'Title Line 2', type: 'text', value: 'Afro-Brasilian Cultural School' },
          { id: 'hero_description', label: 'Description', type: 'textarea', value: 'Experience the authentic rhythms of Brasil. Curated by Mestre Curtis Pierre, preserving 38 years of cultural legacy through Capoeira and Brasilian percussion.' },
          { id: 'hero_btn_1', label: 'Button 1 Text', type: 'text', value: 'Browse Music' },
          { id: 'hero_btn_2', label: 'Button 2 Text', type: 'text', value: 'Our Story' },
        ]
      },
      {
        id: 'featured',
        name: 'Featured Music',
        icon: Layers,
        elements: [
          { id: 'featured_title', label: 'Section Title', type: 'text', value: 'Featured Music' },
          { id: 'featured_subtitle', label: 'Subtitle', type: 'text', value: 'Handpicked tracks from our collection' },
        ]
      },
      {
        id: 'about',
        name: 'About Section',
        icon: Type,
        elements: [
          { id: 'about_title', label: 'Section Title', type: 'text', value: 'About Escola de Samba Casa Samba' },
          { id: 'about_text', label: 'About Text', type: 'textarea', value: 'Escola de Samba Casa Samba brings authentic Afro-Brasilian drumming, dance, and culture to audiences worldwide.' },
        ]
      }
    ]
  },
  videos: {
    name: 'Videos',
    sections: [
      {
        id: 'video_hero',
        name: 'Page Header',
        icon: Layout,
        elements: [
          { id: 'video_title', label: 'Page Title', type: 'text', value: 'Video Store' },
          { id: 'video_subtitle', label: 'Subtitle', type: 'textarea', value: 'Watch and learn from the masters of Brasilian percussion' },
        ]
      }
    ]
  },
  courses: {
    name: 'Courses',
    sections: [
      {
        id: 'courses_hero',
        name: 'Page Header',
        icon: Layout,
        elements: [
          { id: 'courses_title', label: 'Page Title', type: 'text', value: 'Escola de Samba Casa Samba Academy' },
          { id: 'courses_subtitle', label: 'Subtitle', type: 'textarea', value: 'Master the art of Afro-Brasilian drumming, dance, and culture with world-class instruction' },
        ]
      }
    ]
  },
  membership: {
    name: 'Membership',
    sections: [
      {
        id: 'membership_hero',
        name: 'Page Header',
        icon: Layout,
        elements: [
          { id: 'membership_title', label: 'Page Title', type: 'text', value: 'Membership Plans' },
          { id: 'membership_subtitle', label: 'Subtitle', type: 'textarea', value: 'Join our community and unlock exclusive content' },
        ]
      }
    ]
  }
};

export const AdminVisualEditor = () => {
  const navigate = useNavigate();
  const [activePage, setActivePage] = useState('home');
  const [selectedSection, setSelectedSection] = useState(null);
  const [selectedElement, setSelectedElement] = useState(null);
  const [content, setContent] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [previewMode, setPreviewMode] = useState('desktop');
  const [showNavigator, setShowNavigator] = useState(true);
  
  // Refs for scrolling to sections
  const previewContainerRef = useRef(null);
  const sectionRefs = useRef({});

  const currentPageData = PAGE_SECTIONS[activePage];

  // Scroll to section when selected
  const scrollToSection = (sectionId) => {
    const sectionEl = sectionRefs.current[sectionId];
    
    if (sectionEl) {
      sectionEl.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
        inline: 'nearest'
      });
    }
  };

  // Handle section selection with scroll
  const handleSectionSelect = (sectionId) => {
    setSelectedSection(sectionId);
    setSelectedElement(null);
    // Scroll after a brief delay to allow state update
    setTimeout(() => scrollToSection(sectionId), 100);
  };

  useEffect(() => {
    if (!isAdminAuthenticated()) {
      navigate('/admin');
      return;
    }
    loadContent();
  }, [activePage]);

  const loadContent = async () => {
    setLoading(true);
    try {
      const response = await adminFetch('/admin/site-content');
      if (response.ok) {
        const data = await response.json();
        const contentMap = {};
        data.forEach(item => {
          contentMap[item.key] = item.value;
        });
        setContent(contentMap);
      }
    } catch (error) {
      console.error('Failed to load content');
    } finally {
      setLoading(false);
    }
  };

  const getElementValue = (elementId, defaultValue) => {
    return content[elementId] || defaultValue;
  };

  const handleElementChange = (elementId, value) => {
    setContent(prev => ({ ...prev, [elementId]: value }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const updates = Object.entries(content).map(([key, value]) => ({
        key,
        value,
        section: activePage
      }));

      const response = await adminFetch('/admin/site-content', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates })
      });

      if (response.ok) {
        toast.success('Changes saved successfully!');
        setHasChanges(false);
      }
    } catch (error) {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const renderPreview = () => {
    const sections = currentPageData.sections;
    
    return (
      <div className="bg-[#1a1a1a] min-h-full">
        {/* Preview of Homepage */}
        {activePage === 'home' && (
          <>
            {/* Hero Section Preview */}
            <div 
              ref={el => sectionRefs.current['hero'] = el}
              id="section-hero"
              className={`relative min-h-[500px] bg-gradient-to-r from-black/80 to-black/40 p-12 cursor-pointer transition-all ${selectedSection === 'hero' ? 'ring-2 ring-primary' : 'hover:ring-1 hover:ring-primary/50'}`}
              onClick={() => handleSectionSelect('hero')}
              style={{
                backgroundImage: 'url(https://images.unsplash.com/photo-1533174072545-7a4b6ad7a6c3?w=1200)',
                backgroundSize: 'cover',
                backgroundPosition: 'center'
              }}
            >
              <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/70 to-transparent" />
              <div className="relative z-10 max-w-2xl">
                <span 
                  className={`inline-block px-3 py-1 bg-primary/20 text-primary text-sm rounded-full mb-4 cursor-text ${selectedElement === 'hero_badge' ? 'ring-2 ring-blue-500' : ''}`}
                  onClick={(e) => { e.stopPropagation(); setSelectedElement('hero_badge'); }}
                >
                  {getElementValue('hero_badge', 'AFRO-BRAZILIAN MUSIC')}
                </span>
                
                <h1 
                  className={`text-6xl font-bold text-white mb-2 cursor-text ${selectedElement === 'hero_title' ? 'ring-2 ring-blue-500' : ''}`}
                  onClick={(e) => { e.stopPropagation(); setSelectedElement('hero_title'); }}
                >
                  {getElementValue('hero_title', 'Escola de Samba Casa Samba')}
                </h1>
                
                <h2 
                  className={`text-5xl font-bold text-primary mb-6 cursor-text ${selectedElement === 'hero_title_2' ? 'ring-2 ring-blue-500' : ''}`}
                  onClick={(e) => { e.stopPropagation(); setSelectedElement('hero_title_2'); }}
                >
                  {getElementValue('hero_title_2', 'Afro-Brasilian Cultural School')}
                </h2>
                
                <p 
                  className={`text-gray-300 text-lg mb-8 max-w-xl cursor-text ${selectedElement === 'hero_description' ? 'ring-2 ring-blue-500 p-2' : ''}`}
                  onClick={(e) => { e.stopPropagation(); setSelectedElement('hero_description'); }}
                >
                  {getElementValue('hero_description', 'Experience the authentic rhythms of Brasil.')}
                </p>
                
                <div className="flex gap-4">
                  <button 
                    className={`px-6 py-3 bg-primary text-black font-semibold rounded-full cursor-text ${selectedElement === 'hero_btn_1' ? 'ring-2 ring-blue-500' : ''}`}
                    onClick={(e) => { e.stopPropagation(); setSelectedElement('hero_btn_1'); }}
                  >
                    {getElementValue('hero_btn_1', 'Browse Music')}
                  </button>
                  <button 
                    className={`px-6 py-3 border border-white/30 text-white font-semibold rounded-full cursor-text ${selectedElement === 'hero_btn_2' ? 'ring-2 ring-blue-500' : ''}`}
                    onClick={(e) => { e.stopPropagation(); setSelectedElement('hero_btn_2'); }}
                  >
                    {getElementValue('hero_btn_2', 'Our Story')}
                  </button>
                </div>
              </div>
              
              {selectedSection === 'hero' && (
                <div className="absolute top-2 left-2 bg-primary text-black text-xs px-2 py-1 rounded font-semibold">
                  Hero Section
                </div>
              )}
            </div>

            {/* Featured Section Preview */}
            <div 
              ref={el => sectionRefs.current['featured'] = el}
              id="section-featured"
              className={`p-12 cursor-pointer transition-all relative ${selectedSection === 'featured' ? 'ring-2 ring-primary' : 'hover:ring-1 hover:ring-primary/50'}`}
              onClick={() => handleSectionSelect('featured')}
            >
              <h2 
                className={`text-3xl font-bold text-white mb-2 cursor-text ${selectedElement === 'featured_title' ? 'ring-2 ring-blue-500' : ''}`}
                onClick={(e) => { e.stopPropagation(); setSelectedElement('featured_title'); }}
              >
                {getElementValue('featured_title', 'Featured Music')}
              </h2>
              <p 
                className={`text-gray-400 cursor-text ${selectedElement === 'featured_subtitle' ? 'ring-2 ring-blue-500' : ''}`}
                onClick={(e) => { e.stopPropagation(); setSelectedElement('featured_subtitle'); }}
              >
                {getElementValue('featured_subtitle', 'Handpicked tracks from our collection')}
              </p>
              
              {/* Placeholder cards */}
              <div className="grid grid-cols-3 gap-6 mt-8">
                {[1,2,3].map(i => (
                  <div key={i} className="bg-white/5 rounded-xl p-4 h-48 flex items-center justify-center text-gray-500">
                    Music Track {i}
                  </div>
                ))}
              </div>
              
              {selectedSection === 'featured' && (
                <div className="absolute top-2 left-2 bg-primary text-black text-xs px-2 py-1 rounded font-semibold">
                  Featured Section
                </div>
              )}
            </div>

            {/* About Section Preview */}
            <div 
              ref={el => sectionRefs.current['about'] = el}
              id="section-about"
              className={`p-12 bg-zinc-900/50 cursor-pointer transition-all relative ${selectedSection === 'about' ? 'ring-2 ring-primary' : 'hover:ring-1 hover:ring-primary/50'}`}
              onClick={() => handleSectionSelect('about')}
            >
              <h2 
                className={`text-3xl font-bold text-white mb-4 cursor-text ${selectedElement === 'about_title' ? 'ring-2 ring-blue-500' : ''}`}
                onClick={(e) => { e.stopPropagation(); setSelectedElement('about_title'); }}
              >
                {getElementValue('about_title', 'About Escola de Samba Casa Samba')}
              </h2>
              <p 
                className={`text-gray-400 text-lg max-w-2xl cursor-text ${selectedElement === 'about_text' ? 'ring-2 ring-blue-500 p-2' : ''}`}
                onClick={(e) => { e.stopPropagation(); setSelectedElement('about_text'); }}
              >
                {getElementValue('about_text', 'Escola de Samba Casa Samba brings authentic Afro-Brasilian drumming, dance, and culture to audiences worldwide.')}
              </p>
              
              {selectedSection === 'about' && (
                <div className="absolute top-2 left-2 bg-primary text-black text-xs px-2 py-1 rounded font-semibold">
                  About Section
                </div>
              )}
            </div>
          </>
        )}

        {/* Other pages */}
        {activePage !== 'home' && currentPageData.sections.map(section => (
          <div 
            key={section.id}
            ref={el => sectionRefs.current[section.id] = el}
            id={`section-${section.id}`}
            className={`p-12 cursor-pointer transition-all relative ${selectedSection === section.id ? 'ring-2 ring-primary' : 'hover:ring-1 hover:ring-primary/50'}`}
            onClick={() => handleSectionSelect(section.id)}
          >
            {section.elements.map(el => (
              <div key={el.id} className="mb-4">
                {el.type === 'text' && el.label.includes('Title') && (
                  <h1 
                    className={`text-4xl font-bold text-white cursor-text ${selectedElement === el.id ? 'ring-2 ring-blue-500' : ''}`}
                    onClick={(e) => { e.stopPropagation(); setSelectedElement(el.id); }}
                  >
                    {getElementValue(el.id, el.value)}
                  </h1>
                )}
                {el.type === 'textarea' && (
                  <p 
                    className={`text-gray-400 text-lg cursor-text ${selectedElement === el.id ? 'ring-2 ring-blue-500 p-2' : ''}`}
                    onClick={(e) => { e.stopPropagation(); setSelectedElement(el.id); }}
                  >
                    {getElementValue(el.id, el.value)}
                  </p>
                )}
              </div>
            ))}
          </div>
        ))}
      </div>
    );
  };

  const getSelectedElementData = () => {
    if (!selectedElement) return null;
    for (const section of currentPageData.sections) {
      const element = section.elements.find(el => el.id === selectedElement);
      if (element) return element;
    }
    return null;
  };

  const selectedElementData = getSelectedElementData();

  return (
    <div className="h-screen bg-[#0d0d0d] flex flex-col overflow-hidden">
      {/* Top Toolbar - Elementor Style */}
      <div className="h-14 bg-[#1a1a1a] border-b border-[#333] flex items-center justify-between px-4">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/admin/dashboard')}
            className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm">Exit</span>
          </button>
          
          <div className="h-6 w-px bg-[#333]" />
          
          <select
            value={activePage}
            onChange={(e) => { setActivePage(e.target.value); setSelectedSection(null); setSelectedElement(null); }}
            className="bg-[#2a2a2a] text-white text-sm px-3 py-1.5 rounded border border-[#444] focus:outline-none focus:border-primary"
          >
            {Object.entries(PAGE_SECTIONS).map(([key, page]) => (
              <option key={key} value={key}>{page.name}</option>
            ))}
          </select>
        </div>

        <div className="flex items-center gap-2">
          {/* Preview Size */}
          <div className="flex items-center bg-[#2a2a2a] rounded p-1">
            <button 
              onClick={() => setPreviewMode('desktop')}
              className={`p-1.5 rounded ${previewMode === 'desktop' ? 'bg-primary text-black' : 'text-gray-400 hover:text-white'}`}
            >
              <Monitor className="w-4 h-4" />
            </button>
            <button 
              onClick={() => setPreviewMode('tablet')}
              className={`p-1.5 rounded ${previewMode === 'tablet' ? 'bg-primary text-black' : 'text-gray-400 hover:text-white'}`}
            >
              <Tablet className="w-4 h-4" />
            </button>
            <button 
              onClick={() => setPreviewMode('mobile')}
              className={`p-1.5 rounded ${previewMode === 'mobile' ? 'bg-primary text-black' : 'text-gray-400 hover:text-white'}`}
            >
              <Smartphone className="w-4 h-4" />
            </button>
          </div>

          <div className="h-6 w-px bg-[#333]" />

          {/* Save Button */}
          <button
            onClick={handleSave}
            disabled={saving || !hasChanges}
            className={`flex items-center gap-2 px-4 py-1.5 rounded text-sm font-medium transition-all ${
              hasChanges 
                ? 'bg-primary text-black hover:bg-primary/90' 
                : 'bg-[#2a2a2a] text-gray-500 cursor-not-allowed'
            }`}
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {saving ? 'Saving...' : hasChanges ? 'Save' : 'Saved'}
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left Panel - Navigator & Editor */}
        <div className="w-72 bg-[#1a1a1a] border-r border-[#333] flex flex-col">
          {/* Panel Tabs */}
          <div className="flex border-b border-[#333]">
            <button
              onClick={() => setShowNavigator(true)}
              className={`flex-1 py-3 text-sm font-medium transition-colors ${showNavigator ? 'text-primary border-b-2 border-primary' : 'text-gray-400 hover:text-white'}`}
            >
              Navigator
            </button>
            <button
              onClick={() => setShowNavigator(false)}
              className={`flex-1 py-3 text-sm font-medium transition-colors ${!showNavigator ? 'text-primary border-b-2 border-primary' : 'text-gray-400 hover:text-white'}`}
            >
              Editor
            </button>
          </div>

          {showNavigator ? (
            /* Navigator - Section List */
            <div className="flex-1 overflow-y-auto p-3">
              <p className="text-xs text-gray-500 uppercase tracking-wider mb-3">Sections</p>
              {currentPageData.sections.map(section => (
                <div key={section.id} className="mb-2">
                  <button
                    onClick={() => handleSectionSelect(section.id)}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all ${
                      selectedSection === section.id 
                        ? 'bg-primary/20 text-primary' 
                        : 'text-gray-300 hover:bg-[#2a2a2a]'
                    }`}
                  >
                    <section.icon className="w-4 h-4" />
                    <span className="text-sm font-medium">{section.name}</span>
                    <ChevronRight className={`w-4 h-4 ml-auto transition-transform ${selectedSection === section.id ? 'rotate-90' : ''}`} />
                  </button>
                  
                  {/* Elements under section */}
                  <AnimatePresence>
                    {selectedSection === section.id && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        className="overflow-hidden"
                      >
                        <div className="pl-6 py-2 space-y-1">
                          {section.elements.map(el => (
                            <button
                              key={el.id}
                              onClick={() => setSelectedElement(el.id)}
                              className={`w-full text-left px-3 py-2 rounded text-sm transition-all ${
                                selectedElement === el.id 
                                  ? 'bg-blue-500/20 text-blue-400' 
                                  : 'text-gray-400 hover:bg-[#2a2a2a] hover:text-white'
                              }`}
                            >
                              {el.label}
                            </button>
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ))}
            </div>
          ) : (
            /* Editor - Edit Selected Element */
            <div className="flex-1 overflow-y-auto p-4">
              {selectedElementData ? (
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-white font-semibold">{selectedElementData.label}</h3>
                    <button 
                      onClick={() => setSelectedElement(null)}
                      className="text-gray-400 hover:text-white"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                  
                  {selectedElementData.type === 'textarea' ? (
                    <textarea
                      value={getElementValue(selectedElementData.id, selectedElementData.value)}
                      onChange={(e) => handleElementChange(selectedElementData.id, e.target.value)}
                      rows={5}
                      className="w-full bg-[#2a2a2a] border border-[#444] rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-primary resize-none"
                      placeholder={`Enter ${selectedElementData.label.toLowerCase()}...`}
                    />
                  ) : (
                    <input
                      type="text"
                      value={getElementValue(selectedElementData.id, selectedElementData.value)}
                      onChange={(e) => handleElementChange(selectedElementData.id, e.target.value)}
                      className="w-full bg-[#2a2a2a] border border-[#444] rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-primary"
                      placeholder={`Enter ${selectedElementData.label.toLowerCase()}...`}
                    />
                  )}
                  
                  <p className="text-xs text-gray-500 mt-2">
                    Click on the element in the preview to select it, then edit here.
                  </p>
                </div>
              ) : selectedSection ? (
                <div className="text-center py-8">
                  <MousePointer className="w-8 h-8 text-gray-500 mx-auto mb-3" />
                  <p className="text-gray-400 text-sm">Click on an element in the preview to edit it</p>
                </div>
              ) : (
                <div className="text-center py-8">
                  <Layout className="w-8 h-8 text-gray-500 mx-auto mb-3" />
                  <p className="text-gray-400 text-sm">Select a section from the Navigator</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Right Panel - Live Preview */}
        <div ref={previewContainerRef} className="flex-1 bg-[#0d0d0d] overflow-auto flex justify-center p-6">
          <div 
            className={`bg-[#1a1a1a] rounded-lg shadow-2xl overflow-hidden transition-all duration-300 ${
              previewMode === 'mobile' ? 'w-[375px]' : 
              previewMode === 'tablet' ? 'w-[768px]' : 
              'w-full max-w-[1200px]'
            }`}
            style={{ minHeight: '600px' }}
          >
            {loading ? (
              <div className="flex items-center justify-center h-96">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
              </div>
            ) : (
              renderPreview()
            )}
          </div>
        </div>
      </div>

      {/* Bottom Status Bar */}
      <div className="h-8 bg-[#1a1a1a] border-t border-[#333] flex items-center justify-between px-4 text-xs text-gray-500">
        <div className="flex items-center gap-4">
          <span>Page: {currentPageData.name}</span>
          {selectedSection && <span>Section: {currentPageData.sections.find(s => s.id === selectedSection)?.name}</span>}
          {selectedElement && <span>Editing: {selectedElementData?.label}</span>}
        </div>
        <div className="flex items-center gap-2">
          {hasChanges && <span className="text-primary">Unsaved changes</span>}
        </div>
      </div>
    </div>
  );
};

export default AdminVisualEditor;
